import React from 'react';
import { ComingSoon } from '../base/coming-soon';
import { DetailField, DetailSection, DetailUnavailable, DetailPanelStyles as S } from '../base/detail-panel';
import { assembleName } from '../badge/util';

/**
 * Everything we actually hold about one provider on a claim (FE-9).
 *
 * ## Half the design is not backed, and this file is where that is decided
 *
 * The mockup shows a fully-credentialed rendering provider with ten fields. Five of them have
 * no field on `ProviderProvider` — checked against all 29 of them, not inferred:
 *
 * | design field | status |
 * | -- | -- |
 * | NPI · State license · Taxonomy · Direct phone | backed |
 * | DEA | ABSENT — nearest are `ptan`, `upin`, `commercialNumber`, `wcb`, `other`, none of which is a DEA registration |
 * | Payer enrollment · Effective date · Supervising provider | ABSENT — there is no credentialing model here at all |
 * | Practice-location facility | ABSENT — no provider→facility link; the claim's facility is a different entity |
 *
 * Two more that the ticket did not catch, both in the header subtitle:
 *
 * - **"Type 1 NPI"** — an NPPES *enumeration* type, fixed when the NPI is issued. It is NOT
 *   derivable from having a person name rather than an organization name: a sole proprietor
 *   holds a Type 1 NPI under a business name. Rendering it would assert an enrolment fact.
 * - **"Physical Medicine & Rehabilitation"** — the specialty display name. We hold
 *   `taxonomyQualifier`, a code qualifier, not a label for it.
 *
 * So the unbacked fields render `Not available` behind a hint, never a plausible value and
 * never a blank. On a claims console an invented "Payer enrollment: Approved" is not a
 * cosmetic defect — it asserts a credentialing fact nobody verified (NS-4).
 *
 * ## Structural props, like the payer drawer
 *
 * Takes the fields it reads rather than `ProviderProvider`, so the support console can render
 * the same dialog from its own model without either app's type appearing in core.
 *
 * ## Content only — the shell comes from `DialogContext`
 *
 * Every other dialog on this screen (`ProviderCloseoutDialog`, `CollectionPaymentDialog`,
 * `InvoiceItemDialog`) is passed as `Component` to `dialogContext.showDialog`, which supplies
 * the `BaseDialog`, the title and the close button. Wrapping our own would nest two dialogs
 * and give this one a different close affordance from every neighbour.
 */

/** The provider fields this dialog reads. Structural on purpose — see the header. */
export interface IDetailProvider {
    id?: string;
    first?: string;
    last?: string;
    suffix?: string;
    organization?: string;
    /** The claim ROLE — BILLING, RENDERING, ATTENDING… — not individual-vs-organization. */
    type?: string;
    npi?: string;
    license?: string;
    taxonomyCode?: string;
    taxonomyQualifier?: string;
    phone?: string;
}

export interface IProviderDetailProps {
    provider?: IDetailProvider;
}

const CREDENTIALING_HINT =
    'payer enrolment, effective dates and supervising provider are not exposed by the API yet';
const DEA_HINT = 'the API exposes no DEA registration field';

/** `207R00000X · qualifier ZZ` — the qualifier is shown as what it is, never as a specialty name. */
const taxonomyOf = (provider: IDetailProvider): string | undefined => {
    if (!provider.taxonomyCode) {
        return undefined;
    }
    return provider.taxonomyQualifier
        ? `${provider.taxonomyCode} · qualifier ${provider.taxonomyQualifier}`
        : provider.taxonomyCode;
};

export const ProviderDetail: React.FunctionComponent<IProviderDetailProps> = ({
    provider,
}: IProviderDetailProps): React.ReactElement | null => {
    if (!provider) {
        return null;
    }

    const person = assembleName(provider.first, provider.last, provider.suffix);
    // Falls back to the organization, then to nothing — never to "Unknown provider", which
    // reads as a claim about the provider rather than about what we were given.
    const name = person || provider.organization;

    return (
        <S.Body>
            {/*
              * No eyebrow here. `dialogContext.showDialog` already renders the ROLE as the
              * dialog's title, so repeating it read as "Billing Provider / BILLING PROVIDER"
              * stacked. The eyebrow belongs to the drawer, which has no title bar of its own.
              */}
            <S.Section>
                <S.TitleRow>
                    <S.Title>{name || 'Provider'}</S.Title>
                </S.TitleRow>
                {person && provider.organization && <S.Muted>{provider.organization}</S.Muted>}
            </S.Section>

            <DetailSection title="Identifiers">
                <DetailField label="NPI" value={provider.npi} />
                <DetailField label="State license" value={provider.license} />
                <DetailField label="Taxonomy" value={taxonomyOf(provider)} />
                <DetailField label="DEA" unavailable={DEA_HINT} />
            </DetailSection>

            {/*
              * Every credentialing field is missing for ONE reason, so it is stated once. Three
              * rows reading "Not available" implied three lookups that each failed, and made a
              * correctly-scoped dialog look broken.
              */}
            <DetailSection
                title="Credentialing"
                meta={
                    <ComingSoon hint={CREDENTIALING_HINT}>
                        <span>Coming soon</span>
                    </ComingSoon>
                }
            >
                <DetailUnavailable>
                    Payer enrolment, effective dates and supervising provider are not exposed by the API yet.
                </DetailUnavailable>
            </DetailSection>

            {/* Partly backed, so the phone stays a row and only the missing link is a note. */}
            <DetailSection title="Practice location">
                <DetailField label="Direct phone" value={provider.phone} />
                <DetailUnavailable>
                    Providers are not linked to a practice location. The facility on a claim is where the
                    service happened, which is not necessarily where this provider practises.
                </DetailUnavailable>
            </DetailSection>
        </S.Body>
    );
};
